import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addBannerImage,
  createFaculty,
  createGalleryBatch,
  createGalleryItem,
  
  createTestimonial,
  createTrade,
  deleteBannerImage,
  deletePrincipalImage,
  deleteAdmissionPdf,
  deleteEnquiry,
  deleteFaculty,
  deleteGalleryItem,
  
  deleteTestimonial,
  deleteTrade,
  fetchAdmissions,
  fetchEnquiries,
  fetchFaculties,
  fetchGallery,
  fetchSiteSettings,
  fetchTestimonials,
  fetchTrades,
  updateAdmissions,
  updatePrincipalImage,
  updateFaculty,
  updateGalleryItem,
  
  updateSiteSettings,
  updateBannerImageAt,
  updateTestimonial,
  updateTrade,
} from "../../api/api";
import { getAssetUrl } from "../../utils/assets";
import { useAuth } from "../../context/AuthContext";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import {
  FiBarChart2,
  FiBell,
  FiChevronDown,
  FiFileText,
  FiImage,
  FiLayers,
  FiLayout,
  FiMail,
  FiSearch,
  FiUsers,
  FiEdit2,
  FiTrash2,
} from "react-icons/fi";
import { AnimatePresence, motion } from "framer-motion";
import {
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import * as XLSX from "xlsx";
import {
  adBadgeNew,
  adCard,
  adDeleteBtn,
  adEditBtn,
  adEmpty,
  adIconBtn,
  adInput,
  adLogoutBtn,
  adMain,
  adNavBtn,
  adNavBtnActive,
  adPill,
  adPrimaryBtn,
  adSecondaryBtn,
  adShell,
  adSidebar,
  adTableWrap,
  adTd,
  adTh,
  adThead,
  adTopbar,
  adTrHover,
} from "./adminDashboardTheme";
import ToastStack from "../../components/ui/ToastStack";

const emptyTrade = { name: "", duration: "", description: "" };
const emptyFaculty = {
  name: "",
  qualification: "",
  role: "",
  trade: "",
  experienceText: "",
  specialization: "",
  experienceYears: "",
};
const GALLERY_CATS = ["Campus", "Workshops", "Students", "Events"];
const revokeObjectUrl = (url) => {
  if (typeof url === "string" && url.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
};

const uniqueById = (items) => {
  const seen = new Set();
  return (Array.isArray(items) ? items : []).filter((item) => {
    const key = String(item?.id || "");
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};


const UPLOAD_COMPLETE_DELAY_MS = 180;

const formatUploadFileLabel = (files, fallbackLabel) => {
  const list = Array.isArray(files) ? files.filter(Boolean) : files ? [files] : [];
  if (!list.length) return fallbackLabel;
  if (list.length === 1) return list[0]?.name || fallbackLabel;
  return `${list[0]?.name || fallbackLabel} +${list.length - 1} more`;
};

const LEGACY_ADMISSION_PDF_FIELDS = [
  "admissionPdfUrl",
  "admissionBrochurePdfUrl",
  "brochurePdfUrl",
  "pdf",
];
const ADMISSION_PDF_FIELDS = ["pdfUrl", ...LEGACY_ADMISSION_PDF_FIELDS];

const readTrimmedString = (value) => (typeof value === "string" ? value.trim() : "");

const resolveAdmissionPdfField = (info) => {
  for (const field of ADMISSION_PDF_FIELDS) {
    if (readTrimmedString(info?.[field])) return field;
  }
  return "pdfUrl";
};

const resolveAdmissionPdfUrl = (info) => {
  const field = resolveAdmissionPdfField(info);
  return readTrimmedString(info?.[field]);
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [activeSection, setActiveSection] = useState("dashboard");
  const [trades, setTrades] = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [admissionsInfo, setAdmissionsInfo] = useState({});
  const [admissionForm, setAdmissionForm] = useState({
    admissionOpenDate: "",
    admissionCloseDate: "",
    academicYear: "",
    verificationStartDate: "",
    verificationEndDate: "",
    notice: "",
  });
  const [admissionPdfFile, setAdmissionPdfFile] = useState(null);
  const [tentativeCalendarPdfFile, setTentativeCalendarPdfFile] = useState(null);
  const [bannerImages, setBannerImages] = useState([]);
  const [principalImage, setPrincipalImage] = useState("");
  const [tradeDraft, setTradeDraft] = useState(emptyTrade);
  const [tradeImageFiles, setTradeImageFiles] = useState([]);
  const [tradePreviews, setTradePreviews] = useState([]);
  const [facultyDraft, setFacultyDraft] = useState(emptyFaculty);
  const [facultyImageFile, setFacultyImageFile] = useState(null);
  const [facultyPreview, setFacultyPreview] = useState("");
  const [galleryDraft, setGalleryDraft] = useState({ title: "", category: "Campus" });
  const [galleryImageFile, setGalleryImageFile] = useState(null);
  const [galleryPreview, setGalleryPreview] = useState("");
  const [galleryBatch, setGalleryBatch] = useState({ category: "Campus", titlePrefix: "Gallery", files: [] });
  const [editingGalleryId, setEditingGalleryId] = useState("");
  const [testimonialDraft, setTestimonialDraft] = useState({ name: "", text: "" });
  const [testimonialImageFile, setTestimonialImageFile] = useState(null);
  const [testimonialPreview, setTestimonialPreview] = useState("");
  const [editingTestimonialId, setEditingTestimonialId] = useState("");
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState("");
  const [bannerTargetIndex, setBannerTargetIndex] = useState(0);
  const [placementDraft, setPlacementDraft] = useState({
    studentsTrained: "",
    studentsPlaced: "",
    studentsOfferedToHigherEducation: "",
  });
  const [editingTradeId, setEditingTradeId] = useState("");
  const [editingFacultyId, setEditingFacultyId] = useState("");
  const [activeModal, setActiveModal] = useState("");
  const [toasts, setToasts] = useState([]);
  const [activity, setActivity] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [uploadFileName, setUploadFileName] = useState("");
  const [enquiries, setEnquiries] = useState([]);
  const [enquiryQuery, setEnquiryQuery] = useState("");
  const [enquiryStatus, setEnquiryStatus] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("admin_enquiry_status") || "{}") || {};
    } catch {
      return {};
    }
  });
  const [deleting, setDeleting] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const facultySubmitLock = useRef(false);

  const showToast = (type, text) => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [{ id, type, text }, ...prev].slice(0, 3));
    setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== id)), 3400);
    setActivity((prev) =>
      [
        { id: `${Date.now()}-${Math.random()}`, type, text, at: new Date().toISOString() },
        ...prev,
      ].slice(0, 8)
    );
  };

  const uploadPercent = Math.max(0, Math.min(uploadProgress || 0, 100));
  const admissionPdfUrl = resolveAdmissionPdfUrl(admissionsInfo);

  const beginUpload = (fileName) => {
    setUploadError("");
    setUploadProgress(0);
    setUploadFileName(fileName);
    setSaving(true);
  };

  const finishUpload = async () => {
    setUploadProgress(100);
    await new Promise((resolve) => window.setTimeout(resolve, UPLOAD_COMPLETE_DELAY_MS));
  };

  const resetUploadState = () => {
    setUploadProgress(0);
    setUploadFileName("");
  };

  const getUploadButtonLabel = (idleLabel, activeLabel = "Saving") => {
    if (!saving) return idleLabel;
    return uploadFileName ? `${activeLabel} ${uploadPercent}%` : `${activeLabel}...`;
  };

  const renderUploadFeedback = () => {
    if (!uploadFileName && !uploadError && !saving) return null;

    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50/90 px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900">
              {uploadFileName || "Waiting for upload"}
            </p>
            <p className="text-xs text-slate-500">
              {uploadError ? uploadError : `${uploadPercent}% uploaded`}
            </p>
          </div>
          <span className="shrink-0 text-sm font-semibold text-slate-700">{uploadPercent}%</span>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
          <div
            className={`h-full transition-all duration-200 ${
              uploadError ? "bg-rose-500" : "bg-gradient-to-r from-pink-400 to-yellow-300"
            }`}
            style={{ width: `${uploadError ? 100 : uploadPercent}%` }}
          />
        </div>
      </div>
    );
  };

  const uploadConfig = {
    onUploadProgress: (evt) => {
      if (!evt?.total) return;
      const raw = Math.round((evt.loaded / evt.total) * 100);
      const next = raw >= 100 ? 100 : Math.min(raw, 95);
      setUploadProgress(next);
    },
  };

  const closeModal = useCallback(() => {
    setActiveModal("");
    setEditingTradeId("");
    setEditingFacultyId("");
    setEditingGalleryId("");
    setEditingTestimonialId("");
    setTradeImageFiles([]);
    setTradePreviews([]);
    setFacultyImageFile(null);
    setFacultyPreview("");
    setGalleryImageFile(null);
    setGalleryPreview("");
    setTestimonialImageFile(null);
    setTestimonialPreview("");
    setBannerFile(null);
    setBannerPreview("");
    setBannerTargetIndex(0);
    setUploadError("");
    setUploadProgress(0);
    setUploadFileName("");
    setGalleryBatch({ category: "Campus", titlePrefix: "Gallery", files: [] });
  }, []);

  useEffect(() => {
    return () => {
      tradePreviews.forEach((url) => revokeObjectUrl(url));
    };
  }, [tradePreviews]);

  useEffect(() => {
    return () => {
      revokeObjectUrl(facultyPreview);
    };
  }, [facultyPreview]);

  useEffect(() => {
    return () => {
      revokeObjectUrl(galleryPreview);
    };
  }, [galleryPreview]);

  useEffect(() => {
    return () => {
      revokeObjectUrl(testimonialPreview);
    };
  }, [testimonialPreview]);



  useEffect(() => {
    return () => {
      revokeObjectUrl(bannerPreview);
    };
  }, [bannerPreview]);

  const loadAll = useCallback(async () => {
    setPageLoading(true);
    try {
      const [
        tradesData,
        facultiesData,
        galleryData,
        siteData,
        enquiriesData,
        testimonialsData,
        admissionsData,
      ] = await Promise.all([
        fetchTrades(),
        fetchFaculties(),
        fetchGallery(),
        fetchSiteSettings(),
        fetchEnquiries().catch(() => []),
        fetchTestimonials().catch(() => []),
        fetchAdmissions().catch(() => ({})),
      ]);
      setTrades(tradesData);
      setFaculties(uniqueById(facultiesData));
      setGallery(galleryData);
      setEnquiries(Array.isArray(enquiriesData) ? enquiriesData : []);
      setTestimonials(Array.isArray(testimonialsData) ? testimonialsData : []);
      const adm = admissionsData && typeof admissionsData === "object" ? admissionsData : {};
      setAdmissionsInfo(adm);
      setAdmissionForm({
        admissionOpenDate: adm.admissionOpenDate || "",
        admissionCloseDate: adm.admissionCloseDate || "",
        academicYear: adm.academicYear || "",
        verificationStartDate: adm.verificationStartDate || adm.verificationDates || "",
        verificationEndDate: adm.verificationEndDate || "",
        notice: adm.notice || "",
      });
      setBannerImages(Array.isArray(siteData?.bannerImages) ? siteData.bannerImages : []);
      setPrincipalImage(siteData.principalImage || "");
      setPlacementDraft({
        studentsTrained: siteData.placements?.studentsTrained ?? "",
        studentsPlaced: siteData.placements?.studentsPlaced ?? "",
        studentsOfferedToHigherEducation: siteData.placements?.studentsOfferedToHigherEducation ?? "",
      });
    } catch (error) {
      showToast("error", "Failed to load dashboard data");
    } finally {
      setPageLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const refreshEnquiries = useCallback(async () => {
    try {
      const data = await fetchEnquiries();
      setEnquiries(Array.isArray(data) ? data : []);
    } catch {
      /* keep existing on error */
    }
  }, []);

  useEffect(() => {
    if (activeSection === "enquiries") {
      refreshEnquiries();
    }
  }, [activeSection, refreshEnquiries]);

  const NEW_MS = 1000 * 60 * 60 * 48;
  const isEnquiryNew = (createdAt) => {
    const t = new Date(createdAt || 0).getTime();
    return Date.now() - t < NEW_MS;
  };

  const filteredEnquiries = useMemo(() => {
    const q = enquiryQuery.trim().toLowerCase();
    if (!q) return enquiries;
    return enquiries.filter(
      (e) =>
        (e.name || "").toLowerCase().includes(q) ||
        (e.phone || "").toLowerCase().includes(q) ||
        (e.trade || "").toLowerCase().includes(q)
    );
  }, [enquiries, enquiryQuery]);

  const toggleEnquiryStatus = (id) => {
    setEnquiryStatus((prev) => {
      const next = { ...prev, [id]: prev[id] === "Contacted" ? "Pending" : "Contacted" };
      try {
        localStorage.setItem("admin_enquiry_status", JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const enquiriesByMonth = useMemo(() => {
    const map = new Map();
    (enquiries || []).forEach((e) => {
      const d = new Date(e.createdAt || e.date || Date.now());
      if (Number.isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-8)
      .map(([month, count]) => ({ month, count }));
  }, [enquiries]);

  const tradesDistribution = useMemo(() => {
    const map = new Map();
    (enquiries || []).forEach((e) => {
      const k = (e.trade || "Unknown").trim() || "Unknown";
      map.set(k, (map.get(k) || 0) + 1);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, value]) => ({ name: String(name).slice(0, 18), value }));
  }, [enquiries]);

  const exportEnquiriesToExcel = () => {
    const rows = (enquiries || []).map((e) => ({
      Name: e.name || "",
      Phone: e.phone || "",
      Trade: e.trade || "",
      Message: e.message || "",
      Date: e.createdAt ? new Date(e.createdAt).toLocaleString() : "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Enquiries");
    XLSX.writeFile(wb, `enquiries_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const unreadCount = (enquiries || []).filter(
    (e) => isEnquiryNew(e.createdAt) && (enquiryStatus[e.id] || "Pending") !== "Contacted"
  ).length;

  const runConfirmedDelete = async () => {
    if (!confirmAction) return;
    setDeleting(true);
    try {
      const { type, id, index } = confirmAction;
      if (type === "enquiry") {
        await deleteEnquiry(id);
        setEnquiries((prev) => prev.filter((x) => x.id !== id));
        await refreshEnquiries();
        showToast("success", "Enquiry removed");
      } else if (type === "trade") {
        await deleteTrade(id);
        setTrades((prev) => prev.filter((item) => item.id !== id));
        showToast("success", "Trade deleted");
      } else if (type === "faculty") {
        await deleteFaculty(id);
        setFaculties((prev) => prev.filter((item) => item.id !== id));
        showToast("success", "Faculty deleted");
      } else if (type === "gallery") {
        await deleteGalleryItem(id);
        setGallery((prev) => prev.filter((x) => x.id !== id));
        showToast("success", "Gallery item deleted");
      } else if (type === "testimonial") {
        await deleteTestimonial(id);
        setTestimonials((prev) => prev.filter((x) => x.id !== id));
        showToast("success", "Testimonial deleted");
      } else if (type === "banner" && index != null) {
        const site = await deleteBannerImage(index);
        setBannerImages(Array.isArray(site?.bannerImages) ? site.bannerImages : []);
        showToast("success", "Banner removed");
      } else if (type === "principal-image") {
        const site = await deletePrincipalImage();
        setPrincipalImage(site?.principalImage || "");
        showToast("success", "Principal image removed");
      }
    } catch (error) {
      showToast("error", error?.response?.data?.message || "Delete failed");
    } finally {
      setDeleting(false);
      setConfirmAction(null);
    }
  };

  const submitTrade = async (event) => {
    event.preventDefault();
    if (saving) return;
    const editingId = editingTradeId;
    if (!editingId && tradeImageFiles.length === 0) {
      showToast("error", "Please upload at least 1 image (JPG, PNG, or WebP, max 2MB)");
      return;
    }
    if (tradeImageFiles.length > 3) {
      showToast("error", "Maximum 3 images allowed per trade");
      return;
    }
    const fd = new FormData();
    fd.append("name", tradeDraft.name);
    fd.append("duration", tradeDraft.duration);
    fd.append("description", tradeDraft.description);
    tradeImageFiles.slice(0, 3).forEach((f) => fd.append("images", f));
    beginUpload(formatUploadFileLabel(tradeImageFiles, "trade-images"));
    try {
      const response = editingId
        ? await updateTrade(editingId, fd, uploadConfig)
        : await createTrade(fd, uploadConfig);
      setTrades((prev) =>
        editingId ? prev.map((item) => (item.id === response.id ? response : item)) : [response, ...prev]
      );
      setTradeDraft(emptyTrade);
      await finishUpload();
      closeModal();
      showToast("success", "Trade saved");
    } catch (error) {
      setUploadError("Trade upload failed.");
      showToast("error", error?.response?.data?.message || "Unable to save trade");
    } finally {
      setSaving(false);
      resetUploadState();
    }
  };

  const submitFaculty = async (event) => {
    event.preventDefault();
    if (saving || facultySubmitLock.current) return;
    facultySubmitLock.current = true;
    const editingId = editingFacultyId;
    if (!editingId && !facultyImageFile) {
      showToast("error", "Please upload a photo (JPG, PNG, or WebP, max 2MB)");
      facultySubmitLock.current = false;
      return;
    }
    const fd = new FormData();
    fd.append("name", facultyDraft.name);
    fd.append("qualification", facultyDraft.qualification);
    fd.append("role", facultyDraft.role);
    fd.append("trade", facultyDraft.trade || "");
    fd.append("experienceText", facultyDraft.experienceText || "");
    fd.append("specialization", facultyDraft.specialization || "");
    fd.append("experienceYears", facultyDraft.experienceYears || "");
    if (facultyImageFile) fd.append("image", facultyImageFile);
    beginUpload(formatUploadFileLabel(facultyImageFile, "faculty-image"));
    try {
      const response = editingId
        ? await updateFaculty(editingId, fd, uploadConfig)
        : await createFaculty(fd, uploadConfig);
      setFaculties((prev) => {
        const next = editingId
          ? prev.map((item) => (item.id === response.id ? response : item))
          : [response, ...prev];
        return uniqueById(next);
      });
      setFacultyDraft(emptyFaculty);
      await finishUpload();
      closeModal();
      showToast("success", "Faculty saved");
    } catch (error) {
      setUploadError("Faculty upload failed.");
      showToast("error", error?.response?.data?.message || "Unable to save faculty");
    } finally {
      setSaving(false);
      resetUploadState();
      facultySubmitLock.current = false;
    }
  };

  const submitGallery = async (event) => {
    event.preventDefault();
    if (saving) return;
    const editId = editingGalleryId;
    const wasEdit = Boolean(editId);
    if (!wasEdit && !galleryImageFile) {
      showToast("error", "Please upload an image");
      return;
    }
    const fd = new FormData();
    fd.append("title", galleryDraft.title);
    fd.append("category", galleryDraft.category);
    if (galleryImageFile) fd.append("image", galleryImageFile);
    beginUpload(formatUploadFileLabel(galleryImageFile, "gallery-image"));
    try {
      const response = wasEdit
        ? await updateGalleryItem(editId, fd, uploadConfig)
        : await createGalleryItem(fd, uploadConfig);
      setGallery((prev) =>
        wasEdit ? prev.map((x) => (x.id === response.id ? response : x)) : [response, ...prev]
      );
      setGalleryDraft({ title: "", category: "Campus" });
      await finishUpload();
      closeModal();
      showToast("success", wasEdit ? "Gallery updated" : "Gallery image added");
    } catch (error) {
      setUploadError("Gallery upload failed.");
      showToast("error", error?.response?.data?.message || "Unable to save gallery image");
    } finally {
      setSaving(false);
      resetUploadState();
    }
  };

  const submitTestimonial = async (event) => {
    event.preventDefault();
    if (saving) return;
    const editingId = editingTestimonialId;
    if (!editingId && !testimonialImageFile) {
      showToast("error", "Please upload an image (JPG, PNG, or WebP, max 2MB)");
      return;
    }
    const fd = new FormData();
    fd.append("name", testimonialDraft.name || "");
    fd.append("text", testimonialDraft.text || "");
    if (testimonialImageFile) fd.append("image", testimonialImageFile);
    beginUpload(formatUploadFileLabel(testimonialImageFile, "story-image"));
    try {
      const response = editingId
        ? await updateTestimonial(editingId, fd, uploadConfig)
        : await createTestimonial(fd, uploadConfig);
      setTestimonials((prev) =>
        editingId ? prev.map((x) => (x.id === response.id ? response : x)) : [response, ...prev]
      );
      setTestimonialDraft({ name: "", text: "" });
      await finishUpload();
      closeModal();
      showToast("success", "Story saved");
    } catch (error) {
      setUploadError("Story upload failed.");
      showToast("error", error?.response?.data?.message || "Unable to save testimonial");
    } finally {
      setSaving(false);
      resetUploadState();
    }
  };

  
  const MAX_PDF_BYTES = 10 * 1024 * 1024;
  const validatePdfFile = (file) => {
    if (!file) return "";
    if (file.size > MAX_PDF_BYTES) return "File too large. Max 10MB.";
    if (file.type !== "application/pdf") return "Only PDF files are allowed.";
    return "";
  };

  const submitAdmissions = async (event) => {
    event.preventDefault();
    if (saving) return;
    const pdfToValidate = [
      { file: admissionPdfFile, label: "Admission brochure PDF" },
      { file: tentativeCalendarPdfFile, label: "Tentative calendar PDF" },
    ];
    for (const item of pdfToValidate) {
      const validationError = validatePdfFile(item.file);
      if (validationError) {
        showToast("error", `${item.label}: ${validationError}`);
        return;
      }
    }
    const fd = new FormData();
    fd.append("admissionOpenDate", admissionForm.admissionOpenDate || "");
    fd.append("admissionCloseDate", admissionForm.admissionCloseDate || "");
    fd.append("academicYear", admissionForm.academicYear || "");
    fd.append("verificationStartDate", admissionForm.verificationStartDate || "");
    fd.append("verificationEndDate", admissionForm.verificationEndDate || "");
    fd.append("notice", admissionForm.notice || "");
    if (admissionPdfFile) fd.append("pdf", admissionPdfFile);
    if (tentativeCalendarPdfFile) fd.append("tentativeCalendarPdf", tentativeCalendarPdfFile);
    beginUpload(
      formatUploadFileLabel(
        [admissionPdfFile, tentativeCalendarPdfFile].filter(Boolean),
        "admission-pdf"
      )
    );
    try {
      const next = await updateAdmissions(fd, uploadConfig);
      setAdmissionsInfo(next);
      setAdmissionPdfFile(null);
      setTentativeCalendarPdfFile(null);
      await finishUpload();
      showToast("success", "Admissions updated");
    } catch (error) {
      setUploadError("PDF upload failed.");
      showToast("error", error?.response?.data?.message || "Unable to save admissions");
    } finally {
      setSaving(false);
      resetUploadState();
    }
  };

  const handleDeleteAdmissionPdf = async (field = "pdfUrl") => {
    const normalizedField =
      typeof field === "string" && field.trim() === "tentativeCalendarPdfUrl"
        ? "tentativeCalendarPdfUrl"
        : "pdfUrl";
    const resolvedField =
      normalizedField === "tentativeCalendarPdfUrl"
        ? "tentativeCalendarPdfUrl"
        : resolveAdmissionPdfField(admissionsInfo);
    const value = readTrimmedString(admissionsInfo?.[resolvedField]);
    if (!value) return;
    const isTentative = normalizedField === "tentativeCalendarPdfUrl";
    const confirmMessage = isTentative
      ? "Remove the tentative calendar PDF from the website? This cannot be undone."
      : "Remove the admission PDF from the website? This cannot be undone.";
    if (!window.confirm(confirmMessage)) return;
    setSaving(true);
    try {
      const next = await deleteAdmissionPdf(resolvedField);
      setAdmissionsInfo(next);
      if (isTentative) {
        setTentativeCalendarPdfFile(null);
      } else {
        setAdmissionPdfFile(null);
      }
      showToast("success", isTentative ? "Tentative calendar PDF removed" : "PDF removed");
    } catch (error) {
      showToast("error", error?.response?.data?.message || "Unable to delete PDF");
    } finally {
      setSaving(false);
    }
  };

  const submitGalleryBatch = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (!galleryBatch.files.length) {
      showToast("error", "Choose one or more images");
      return;
    }
    const fd = new FormData();
    fd.append("category", galleryBatch.category);
    fd.append("titlePrefix", galleryBatch.titlePrefix || "Gallery");
    galleryBatch.files.forEach((f) => fd.append("images", f));
    beginUpload(formatUploadFileLabel(galleryBatch.files, "gallery-batch"));
    try {
      const created = await createGalleryBatch(fd, uploadConfig);
      setGallery((prev) => [...created, ...prev]);
      await finishUpload();
      closeModal();
      showToast("success", `${created.length} image(s) uploaded`);
    } catch (error) {
      setUploadError("Gallery batch upload failed.");
      showToast("error", error?.response?.data?.message || "Batch upload failed");
    } finally {
      setSaving(false);
      resetUploadState();
    }
  };

  const navBtn = (id, label, Icon) => (
    <button
      type="button"
      onClick={() => setActiveSection(id)}
      className={`${adNavBtn} ${activeSection === id ? adNavBtnActive : ""}`}
    >
      {Icon ? <Icon className="h-4 w-4 shrink-0 opacity-95" aria-hidden /> : null}
      {label}
    </button>
  );

  return (
    <>
    <div className={adShell}>
      <aside className={`${adSidebar} min-h-screen`}>
        <nav className="flex flex-1 flex-col gap-1">
          {navBtn("dashboard", "Dashboard", FiLayout)}
          {navBtn("enquiries", "Enquiries", FiMail)}
          {navBtn("trades", "Trades", FiLayers)}
          {navBtn("faculties", "Faculties", FiUsers)}
          {navBtn("gallery", "Gallery", FiImage)}
          {navBtn("testimonials", "Student Success Stories", FiUsers)}
          {navBtn("admissions", "Admissions", FiFileText)}
          {navBtn("banner", "Banner", FiBarChart2)}
        </nav>
        <div className="mt-auto flex flex-col gap-3 border-t border-slate-200/70 pt-4">
          <p
            className="text-center text-[11px] font-semibold tracking-wide leading-snug"
            style={{
              background: "linear-gradient(to right, #f472b6, #facc15)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: "0 0 8px rgba(236, 72, 153, 0.18)",
            }}
          >
            Developed by Ajay T
          </p>
          <button type="button" onClick={logout} className={`${adLogoutBtn} mt-0`}>
            Logout
          </button>
        </div>
      </aside>

      <main className={adMain}>
        <header className={adTopbar}>
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Admin</p>
            <h1 className="truncate text-lg font-semibold tracking-tight text-slate-900 md:text-xl">
              {activeSection === "dashboard"
                ? "Dashboard"
                : activeSection.charAt(0).toUpperCase() + activeSection.slice(1)}
            </h1>
            {pageLoading ? <p className="mt-0.5 text-xs text-slate-500">Loading…</p> : null}
            {saving ? <p className="mt-0.5 text-xs font-semibold text-amber-600">Saving…</p> : null}
            {uploadFileName || uploadError ? (
              <div className="mt-2 max-w-60 rounded-2xl border border-slate-200 bg-white/80 px-3 py-2 shadow-sm">
                <p className="truncate text-[11px] font-semibold text-slate-800">{uploadFileName || "Upload"}</p>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-200">
                  <div
                    className={`h-full transition-all duration-200 ${
                      uploadError ? "bg-rose-500" : "bg-gradient-to-r from-pink-400 to-yellow-300"
                    }`}
                    style={{ width: `${uploadError ? 100 : uploadPercent}%` }}
                  />
                </div>
                <p className={`mt-1 text-[11px] ${uploadError ? "text-rose-600" : "text-slate-500"}`}>
                  {uploadError ? uploadError : `${uploadPercent}%`}
                </p>
              </div>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            <div className={adPill}>
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-r from-pink-400 via-rose-400 to-yellow-300 text-xs font-bold text-white">
                {(user?.email || "A").slice(0, 1).toUpperCase()}
              </span>
              <span className="hidden max-w-[260px] truncate text-slate-800 md:inline">{user?.email}</span>
              <FiChevronDown className="h-4 w-4 text-slate-500" aria-hidden />
            </div>

            <div className="relative">
              <button
                type="button"
                className={adIconBtn}
                onClick={() => setNotifOpen((v) => !v)}
                aria-label="Notifications"
              >
                <FiBell className="h-5 w-5" aria-hidden />
                {unreadCount ? (
                  <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-pink-500 px-1 text-[10px] font-bold text-white">
                    {unreadCount}
                  </span>
                ) : null}
              </button>
              <AnimatePresence>
                {notifOpen ? (
                  <motion.div
                    initial={{ opacity: 0, y: -6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.98 }}
                    transition={{ duration: 0.16 }}
                    className="absolute right-0 mt-2 w-[320px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
                  >
                    <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                      <p className="text-sm font-semibold text-slate-900">Notifications</p>
                      <button
                        type="button"
                        className="text-xs font-semibold text-rose-600 hover:text-rose-700"
                        onClick={() => setNotifOpen(false)}
                      >
                        Close
                      </button>
                    </div>
                    <div className="max-h-[340px] overflow-auto p-2">
                      {(enquiries || [])
                        .slice()
                        .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
                        .slice(0, 6)
                        .map((e) => (
                          <button
                            key={e.id}
                            type="button"
                            className="w-full rounded-xl px-3 py-2 text-left transition hover:bg-slate-50"
                            onClick={() => {
                              setActiveSection("enquiries");
                              setNotifOpen(false);
                            }}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <p className="truncate text-sm font-semibold text-slate-900">
                                {e.name || "New enquiry"}
                              </p>
                              {isEnquiryNew(e.createdAt) ? (
                                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 ring-1 ring-emerald-200">
                                  NEW
                                </span>
                              ) : null}
                            </div>
                            <p className="mt-0.5 truncate text-xs text-slate-600">
                              {e.trade || "—"} • {e.phone || "—"}
                            </p>
                          </button>
                        ))}
                      {enquiries.length === 0 ? (
                        <p className="px-3 py-10 text-center text-sm text-slate-500">No notifications.</p>
                      ) : null}
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </div>
        </header>

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { label: "Total Trades", value: trades.length },
            { label: "Total Faculties", value: faculties.length },
            { label: "Total Gallery", value: gallery.length },
            { label: "Total Enquiries", value: enquiries.length },
          ].map((card) => (
            <motion.article
              key={card.label}
              whileHover={{ y: -4, scale: 1.01 }}
              transition={{ type: "spring", stiffness: 260, damping: 20 }}
              className={`${adCard} relative overflow-hidden`}
            >
              <div
                className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-pink-400 via-rose-400 to-yellow-300"
                aria-hidden
              />
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">{card.label}</p>
              <p className="mt-3 text-3xl font-semibold text-slate-900">{card.value}</p>
              <p className="mt-1 text-xs text-slate-500">Live</p>
            </motion.article>
          ))}
        </section>

        {activeSection === "dashboard" ? (
          <section className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <article className={`${adCard} relative xl:col-span-2`}>
              <h3 className="mb-4 text-sm font-semibold text-slate-900">Enquiries per month</h3>
              {enquiriesByMonth && enquiriesByMonth.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={enquiriesByMonth} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: "1px solid #e2e8f0",
                        background: "rgba(255,255,255,0.96)",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#7C3AED"
                      strokeWidth={3}
                      dot={{ r: 3, fill: "#EC4899", stroke: "#EC4899" }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="py-12 text-center text-sm text-slate-500">No data available for chart.</p>
              )}
            </article>
            <article className={`${adCard} relative`}>
              <h3 className="mb-4 text-sm font-semibold text-slate-900">Trades distribution (from enquiries)</h3>
              {tradesDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Tooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: "1px solid #e2e8f0",
                        background: "rgba(255,255,255,0.96)",
                      }}
                    />
                    <Pie
                      data={tradesDistribution}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={105}
                      innerRadius={56}
                      paddingAngle={2}
                      stroke="#fff"
                      strokeWidth={1}
                      label
                    >
                      {tradesDistribution.map((_, i) => (
                        <Cell key={i} fill={i % 2 === 0 ? "#EC4899" : "#FACC15"} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="py-12 text-center text-sm text-slate-500">No data available for chart.</p>
              )}
            </article>
            <article className={`${adCard} relative xl:col-span-3`}>
              <h3 className="mb-4 text-sm font-semibold text-[#1e293b]">Placement stats (homepage)</h3>
              <form
                className="admin-form max-w-md space-y-3"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (saving) return;
                  setSaving(true);
                  try {
                    const site = await updateSiteSettings({
                      placements: {
                        studentsTrained: Number(placementDraft.studentsTrained) || 0,
                        studentsPlaced: Number(placementDraft.studentsPlaced) || 0,
                        studentsOfferedToHigherEducation:
                          Number(placementDraft.studentsOfferedToHigherEducation) || 0,
                      },
                    });
                    setPlacementDraft({
                      studentsTrained: site.placements?.studentsTrained ?? "",
                      studentsPlaced: site.placements?.studentsPlaced ?? "",
                      studentsOfferedToHigherEducation:
                        site.placements?.studentsOfferedToHigherEducation ?? "",
                    });
                    showToast("success", "Placement stats saved");
                  } catch (err) {
                    showToast("error", err?.response?.data?.message || "Could not save placement stats");
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                <label className="text-sm font-medium text-[#1e293b]">
                  Students trained
                  <input
                    type="number"
                    className={`${adInput} mt-1`}
                    value={placementDraft.studentsTrained}
                    onChange={(e) =>
                      setPlacementDraft((p) => ({ ...p, studentsTrained: e.target.value }))
                    }
                  />
                </label>
                <label className="text-sm font-medium text-[#1e293b]">
                  Students placed
                  <input
                    type="number"
                    className={`${adInput} mt-1`}
                    value={placementDraft.studentsPlaced}
                    onChange={(e) =>
                      setPlacementDraft((p) => ({ ...p, studentsPlaced: e.target.value }))
                    }
                  />
                </label>
                <label className="text-sm font-medium text-[#1e293b]">
                  Students offered to higher education
                  <input
                    type="number"
                    className={`${adInput} mt-1`}
                    value={placementDraft.studentsOfferedToHigherEducation}
                    onChange={(e) =>
                      setPlacementDraft((p) => ({
                        ...p,
                        studentsOfferedToHigherEducation: e.target.value,
                      }))
                    }
                  />
                </label>
                <button type="submit" className={adPrimaryBtn} disabled={saving}>
                  {saving ? "Saving…" : "Save stats"}
                </button>
              </form>
            </article>
          </section>
        ) : null}

        {activeSection === "enquiries" ? (
          <section className={`${adCard} relative p-0`}>
            <div className="flex flex-col gap-4 border-b border-slate-200/70 p-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Admission enquiries</h3>
                <p className="text-sm text-slate-600">Newest first · search by name, phone, trade</p>
              </div>
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                <button type="button" className={`${adPrimaryBtn} text-sm`} onClick={exportEnquiriesToExcel}>
                  Export Excel
                </button>
                <div className="relative w-full sm:w-72">
                  <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
                  <input
                    type="search"
                    placeholder="Search…"
                    value={enquiryQuery}
                    onChange={(e) => setEnquiryQuery(e.target.value)}
                    className={`${adInput} w-full pl-10 text-sm`}
                  />
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className={`${adTableWrap} min-w-[640px] text-left text-sm`}>
                <thead>
                  <tr className={adThead}>
                    <th className={adTh}>Name</th>
                    <th className={adTh}>Phone</th>
                    <th className={adTh}>Trade</th>
                    <th className={adTh}>Date</th>
                    <th className={adTh}>Status</th>
                    <th className={`${adTh} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEnquiries.length === 0 ? (
                    <tr>
                      <td colSpan={6} className={`${adTd} py-14 text-center text-slate-500`}>
                        {enquiries.length === 0
                          ? "No data available — no admission enquiries yet."
                          : "No enquiries match your search."}
                      </td>
                    </tr>
                  ) : (
                    filteredEnquiries.map((row) => (
                      <tr key={row.id} className={adTrHover}>
                        <td className={`${adTd} font-medium text-slate-800`}>
                          <span className="inline-flex flex-wrap items-center gap-2">
                            {row.name}
                            {isEnquiryNew(row.createdAt) ? <span className={adBadgeNew}>New</span> : null}
                          </span>
                        </td>
                        <td className={`${adTd} text-slate-600`}>{row.phone}</td>
                        <td className={`${adTd} text-slate-600`}>{row.trade || "—"}</td>
                        <td className={`${adTd} whitespace-nowrap text-slate-500`}>
                          {row.createdAt
                            ? new Date(row.createdAt).toLocaleString(undefined, {
                                dateStyle: "medium",
                                timeStyle: "short",
                              })
                            : "—"}
                        </td>
                        <td className={adTd}>
                          <button
                            type="button"
                            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ring-1 transition ${
                              (enquiryStatus[row.id] || "Pending") === "Contacted"
                                ? "bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100"
                                : "bg-amber-50 text-amber-700 ring-amber-200 hover:bg-amber-100"
                            }`}
                            onClick={() => toggleEnquiryStatus(row.id)}
                          >
                            {enquiryStatus[row.id] || "Pending"}
                          </button>
                        </td>
                        <td className={`${adTd} text-right`}>
                          <button
                            type="button"
                            onClick={() =>
                              setConfirmAction({
                                type: "enquiry",
                                id: row.id,
                                label: row.name || "this enquiry",
                              })
                            }
                            className={`${adDeleteBtn} text-xs`}
                            disabled={deleting}
                          >
                            <FiTrash2 className="h-3.5 w-3.5" aria-hidden />
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-1 gap-6 border-t border-slate-200/70 p-6 xl:grid-cols-3">
              <article className="xl:col-span-2">
                <h4 className="mb-3 text-sm font-semibold text-slate-900">Recent activity</h4>
                <div className="space-y-2">
                  {activity.length ? (
                    activity.map((a) => (
                      <div
                        key={a.id}
                        className="flex items-start justify-between gap-3 rounded-xl border border-slate-200/70 bg-white/70 px-4 py-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">{a.text}</p>
                          <p className="text-xs text-slate-500">{new Date(a.at).toLocaleString()}</p>
                        </div>
                        <span
                          className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-bold ${
                            a.type === "error"
                              ? "bg-red-50 text-red-700 ring-1 ring-red-200"
                              : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                          }`}
                        >
                          {a.type === "error" ? "ERROR" : "OK"}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="py-10 text-center text-sm text-slate-500">No activity yet.</p>
                  )}
                </div>
              </article>
              <article>
                <h4 className="mb-3 text-sm font-semibold text-slate-900">Quick stats</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                    <span className="text-slate-600">New (48h)</span>
                    <span className="font-semibold text-slate-900">
                      {(enquiries || []).filter((e) => isEnquiryNew(e.createdAt)).length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                    <span className="text-slate-600">Pending</span>
                    <span className="font-semibold text-slate-900">
                      {(enquiries || []).filter((e) => (enquiryStatus[e.id] || "Pending") !== "Contacted").length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                    <span className="text-slate-600">Contacted</span>
                    <span className="font-semibold text-slate-900">
                      {(enquiries || []).filter((e) => (enquiryStatus[e.id] || "Pending") === "Contacted").length}
                    </span>
                  </div>
                </div>
              </article>
            </div>
          </section>
        ) : null}

        {activeSection === "trades" ? (
          <section className={`${adCard} relative`}>
            <div className="admin-list-header mb-4">
              <h3 className="font-display text-lg font-semibold text-slate-900">Trades</h3>
              <button
                type="button"
                className={`${adPrimaryBtn} text-sm`}
                onClick={() => {
                  setTradeDraft(emptyTrade);
                  setTradeImageFiles([]);
                  setTradePreviews([]);
                  setEditingTradeId("");
                  setActiveModal("trade");
                }}
              >
                Add Trade
              </button>
            </div>
            <div className="divide-y divide-slate-100 rounded-xl border border-slate-100">
              {trades.length === 0 ? (
                <div className={`${adEmpty} border-0 py-12 shadow-none`}>No data available — add a trade to get started.</div>
              ) : (
                trades.map((trade) => (
                  <div key={trade.id} className="flex flex-wrap items-center justify-between gap-3 px-2 py-3 transition hover:bg-slate-50/80">
                    <span className="font-medium text-slate-800">{trade.name}</span>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setTradeDraft({
                            name: trade.name,
                            duration: trade.duration,
                            description: trade.description,
                          });
                          setTradeImageFiles([]);
                          setTradePreviews(
                            (Array.isArray(trade.images) && trade.images.length
                              ? trade.images
                              : trade.image
                                ? [trade.image]
                                : []
                            )
                              .slice(0, 3)
                              .map((p) => getAssetUrl(p))
                              .filter(Boolean)
                          );
                          setEditingTradeId(trade.id);
                          setActiveModal("trade");
                        }}
                        className={`${adEditBtn} text-xs`}
                      >
                        <FiEdit2 className="h-3.5 w-3.5" aria-hidden />
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setConfirmAction({ type: "trade", id: trade.id, label: trade.name || "this trade" })
                        }
                        className={`${adDeleteBtn} text-xs`}
                      >
                        <FiTrash2 className="h-3.5 w-3.5" aria-hidden />
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        ) : null}

        {activeSection === "faculties" ? (
          <section className={`${adCard} relative`}>
            <div className="admin-list-header mb-4">
              <h3 className="font-display text-lg font-semibold text-slate-900">Faculties</h3>
              <button
                type="button"
                className={`${adPrimaryBtn} text-sm`}
                onClick={() => {
                  setFacultyDraft(emptyFaculty);
                  setFacultyImageFile(null);
                  setFacultyPreview("");
                  setEditingFacultyId("");
                  setActiveModal("faculty");
                }}
              >
                Add Faculty
              </button>
            </div>
            <div className="divide-y divide-slate-100 rounded-xl border border-slate-100">
              {faculties.length === 0 ? (
                <div className={`${adEmpty} border-0 py-12 shadow-none`}>No data available — add a faculty member.</div>
              ) : (
                faculties.map((faculty) => (
                  <div key={faculty.id} className="flex flex-wrap items-center justify-between gap-3 px-2 py-3 transition hover:bg-slate-50/80">
                    <span className="font-medium text-slate-800">{faculty.name}</span>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setFacultyDraft({
                            name: faculty.name,
                            qualification: faculty.qualification,
                            role: faculty.role,
                            trade: faculty.trade || "",
                            experienceText: faculty.experienceText || "",
                            specialization: faculty.specialization || "",
                            experienceYears: faculty.experienceYears || "",
                          });
                          setFacultyImageFile(null);
                          setFacultyPreview(getAssetUrl(faculty.image) || "");
                          setEditingFacultyId(faculty.id);
                          setActiveModal("faculty");
                        }}
                        className={`${adEditBtn} text-xs`}
                      >
                        <FiEdit2 className="h-3.5 w-3.5" aria-hidden />
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setConfirmAction({
                            type: "faculty",
                            id: faculty.id,
                            label: faculty.name || "this faculty",
                          })
                        }
                        className={`${adDeleteBtn} text-xs`}
                      >
                        <FiTrash2 className="h-3.5 w-3.5" aria-hidden />
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        ) : null}

        {activeSection === "gallery" ? (
          <section className={`${adCard} relative`}>
            <div className="admin-list-header mb-4">
              <h3 className="font-display text-lg font-semibold text-slate-900">Gallery</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className={`${adSecondaryBtn} text-sm`}
                  onClick={() => {
                    setGalleryBatch({ category: "Campus", titlePrefix: "Gallery", files: [] });
                    setActiveModal("gallery-batch");
                  }}
                >
                  Upload multiple
                </button>
                <button
                  type="button"
                  className={`${adPrimaryBtn} text-sm`}
                  onClick={() => {
                    setGalleryDraft({ title: "", category: "Campus" });
                    setGalleryImageFile(null);
                    setGalleryPreview("");
                    setEditingGalleryId("");
                    setActiveModal("gallery");
                  }}
                >
                  Add
                </button>
              </div>
            </div>
            <div className="divide-y divide-slate-100 rounded-xl border border-slate-100">
              {gallery.length === 0 ? (
                <div className={`${adEmpty} border-0 py-12 shadow-none`}>No data available — upload gallery images.</div>
              ) : (
                gallery.map((item) => (
                  <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 px-2 py-3 transition hover:bg-slate-50/80">
                    <span className="font-medium text-slate-800">
                      {item.title}{" "}
                      <small className="text-slate-500">({item.category || "Campus"})</small>
                    </span>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setGalleryDraft({
                            title: item.title,
                            category: item.category || "Campus",
                          });
                          setGalleryImageFile(null);
                          setGalleryPreview(getAssetUrl(item.imageUrl || item.image) || "");
                          setEditingGalleryId(item.id);
                          setActiveModal("gallery");
                        }}
                        className={`${adEditBtn} text-xs`}
                      >
                        <FiEdit2 className="h-3.5 w-3.5" aria-hidden />
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setConfirmAction({
                            type: "gallery",
                            id: item.id,
                            label: item.title || "this item",
                          })
                        }
                        className={`${adDeleteBtn} text-xs`}
                      >
                        <FiTrash2 className="h-3.5 w-3.5" aria-hidden />
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        ) : null}

        {activeSection === "testimonials" ? (
          <section className={`${adCard} relative`}>
            <div className="admin-list-header mb-4">
              <h3 className="font-display text-lg font-semibold text-slate-900">Testimonials</h3>
              <button
                type="button"
                className={`${adPrimaryBtn} text-sm`}
                onClick={() => {
                  setTestimonialDraft({ name: "", text: "" });
                  setTestimonialImageFile(null);
                  setTestimonialPreview("");
                  setEditingTestimonialId("");
                  setActiveModal("testimonial");
                }}
              >
                Add
              </button>
            </div>
            <div className="divide-y divide-slate-100 rounded-xl border border-slate-100">
              {testimonials.length === 0 ? (
                <div className={`${adEmpty} border-0 py-12 shadow-none`}>
                  No data available — add a student success story.
                </div>
              ) : (
                testimonials.map((t) => (
                  <div
                    key={t.id}
                    className="flex flex-wrap items-center justify-between gap-3 px-2 py-3 transition hover:bg-slate-50/80"
                  >
                    <span className="flex items-center gap-3">
                      {t.image ? (
                        <img
                          src={getAssetUrl(t.image)}
                          alt="Student success story"
                          className="h-10 w-10 rounded-xl object-cover ring-1 ring-slate-200"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : null}
                      <span className="font-medium text-slate-800">{t.name || "Story"}</span>
                    </span>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setTestimonialDraft({ name: t.name || "", text: t.text || "" });
                          setTestimonialImageFile(null);
                          setTestimonialPreview(getAssetUrl(t.image) || "");
                          setEditingTestimonialId(t.id);
                          setActiveModal("testimonial");
                        }}
                        className={`${adEditBtn} text-xs`}
                      >
                        <FiEdit2 className="h-3.5 w-3.5" aria-hidden />
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setConfirmAction({
                            type: "testimonial",
                            id: t.id,
                            label: t.name || "this testimonial",
                          })
                        }
                        className={`${adDeleteBtn} text-xs`}
                      >
                        <FiTrash2 className="h-3.5 w-3.5" aria-hidden />
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        ) : null}

        {activeSection === "admissions" ? (
          <section className={`${adCard} relative max-w-3xl`}>
            <h3 className="font-display mb-4 text-lg font-semibold text-slate-900">Admission brochure & dates</h3>
            <form className="admin-form flex flex-col gap-4" onSubmit={submitAdmissions}>
              <label className="flex flex-col gap-2 text-sm font-semibold text-slate-800">
                Notice / instructions
                <textarea
                  className={adInput}
                  rows={4}
                  value={admissionForm.notice}
                  onChange={(e) => setAdmissionForm((f) => ({ ...f, notice: e.target.value }))}
                />
              </label>
              <label className="flex flex-col gap-2 text-sm font-semibold text-slate-800">
                Admission opens (free text / date)
                <input
                  type="text"
                  className={adInput}
                  value={admissionForm.admissionOpenDate}
                  onChange={(e) => setAdmissionForm((f) => ({ ...f, admissionOpenDate: e.target.value }))}
                  placeholder="e.g. April 2026"
                />
              </label>
              <label className="flex flex-col gap-2 text-sm font-semibold text-slate-800">
                Admission closes (free text / date)
                <input
                  type="text"
                  className={adInput}
                  value={admissionForm.admissionCloseDate}
                  onChange={(e) => setAdmissionForm((f) => ({ ...f, admissionCloseDate: e.target.value }))}
                  placeholder="e.g. August 2026"
                />
              </label>
              <label className="flex flex-col gap-2 text-sm font-semibold text-slate-800">
                Academic Year
                <input
                  type="text"
                  className={adInput}
                  value={admissionForm.academicYear}
                  onChange={(e) => setAdmissionForm((f) => ({ ...f, academicYear: e.target.value }))}
                  placeholder="e.g. 2026-27"
                />
              </label>
              <label className="flex flex-col gap-2 text-sm font-semibold text-slate-800">
                Verification start date (free text / date)
                <input
                  type="text"
                  className={adInput}
                  value={admissionForm.verificationStartDate}
                  onChange={(e) => setAdmissionForm((f) => ({ ...f, verificationStartDate: e.target.value }))}
                  placeholder="Shown on the public site when set"
                />
              </label>
              <label className="flex flex-col gap-2 text-sm font-semibold text-slate-800">
                Verification end date (free text / date)
                <input
                  type="text"
                  className={adInput}
                  value={admissionForm.verificationEndDate}
                  onChange={(e) => setAdmissionForm((f) => ({ ...f, verificationEndDate: e.target.value }))}
                  placeholder="Shown on the public site when set"
                />
              </label>
              <label className="flex flex-col gap-2 text-sm font-semibold text-slate-800">
                Replace Admission Brochure PDF (optional — Cloudinary raw upload)
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      const okType = f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
                      if (!okType) {
                        showToast("error", "Only PDF files are allowed.");
                        e.target.value = "";
                        return;
                      }
                      if (f.size > MAX_PDF_BYTES) {
                        showToast("error", "File too large. Max 10MB.");
                        e.target.value = "";
                        return;
                      }
                    }
                    setAdmissionPdfFile(f || null);
                  }}
                />
              </label>
              {admissionPdfUrl ? (
                <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                  <span>
                    Current file:{" "}
                    <a
                      href={admissionPdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-rose-600 underline"
                    >
                      View PDF
                    </a>
                  </span>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => handleDeleteAdmissionPdf("pdfUrl")}
                    className={`${adDeleteBtn} text-xs`}
                  >
                    <FiTrash2 className="inline h-3.5 w-3.5" aria-hidden /> Delete PDF
                  </button>
                </div>
              ) : (
                <p className="text-sm text-slate-500">No PDF uploaded yet.</p>
              )}
              <h4 className="mt-2 text-base font-semibold text-slate-900">Tentative Calendar of Events</h4>
              <label className="flex flex-col gap-2 text-sm font-semibold text-slate-800">
                Upload PDF (optional)
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      const okType = f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
                      if (!okType) {
                        showToast("error", "Only PDF files are allowed.");
                        e.target.value = "";
                        return;
                      }
                      if (f.size > MAX_PDF_BYTES) {
                        showToast("error", "File too large. Max 10MB.");
                        e.target.value = "";
                        return;
                      }
                    }
                    setTentativeCalendarPdfFile(f || null);
                  }}
                />
              </label>
              {admissionsInfo.tentativeCalendarPdfUrl ? (
                <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                  <span>
                    Current tentative calendar:{" "}
                    <a
                      href={(admissionsInfo.tentativeCalendarPdfUrl || "").trim()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-rose-600 underline"
                    >
                      View PDF
                    </a>
                  </span>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => handleDeleteAdmissionPdf("tentativeCalendarPdfUrl")}
                    className={`${adDeleteBtn} text-xs`}
                  >
                    <FiTrash2 className="inline h-3.5 w-3.5" aria-hidden /> Delete PDF
                  </button>
                </div>
              ) : (
                <p className="text-sm text-slate-500">No tentative calendar PDF uploaded yet.</p>
              )}
              {renderUploadFeedback()}
              <button type="submit" className={`${adPrimaryBtn} self-start`} disabled={saving}>
                {getUploadButtonLabel("Save admissions", "Uploading")}
              </button>
            </form>
          </section>
        ) : null}

        {activeSection === "banner" ? (
          <section className={`${adCard} relative`}>
            <div className="admin-list-header mb-4">
              <h3 className="font-display text-lg font-semibold text-slate-900">Banner images</h3>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {[0, 1].map((idx) => {
                const slot = bannerImages[idx];
                const hasBanner = Boolean(slot?.image);
                const bannerSrc = getAssetUrl(slot?.image || "");
                return (
                  <article
                    key={idx}
                    className="rounded-2xl border border-slate-200 bg-white/75 p-4 shadow-sm transition hover:border-slate-300"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h4 className="text-base font-semibold text-slate-900">Banner {idx + 1}</h4>
                        <p className="text-sm text-slate-500">
                          {idx === 0 ? "Primary homepage banner." : "Secondary homepage banner."}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                      {bannerSrc ? (
                        <img
                          src={bannerSrc}
                          alt={`Banner ${idx + 1}`}
                          className="h-40 w-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="flex h-40 items-center justify-center text-sm font-medium text-slate-400">
                          No banner uploaded
                        </div>
                      )}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        className={`${adPrimaryBtn} text-xs`}
                        disabled={saving}
                        onClick={() => {
                          setBannerFile(null);
                          setBannerPreview("");
                          setBannerTargetIndex(idx);
                          setActiveModal("banner");
                        }}
                      >
                        Upload
                      </button>
                      <button
                        type="button"
                        className={`${adSecondaryBtn} text-xs`}
                        disabled={saving || !hasBanner}
                        onClick={() => {
                          setBannerFile(null);
                          setBannerPreview("");
                          setBannerTargetIndex(idx);
                          setActiveModal("banner");
                        }}
                      >
                        Replace
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setConfirmAction({
                            type: "banner",
                            index: idx,
                            id: `banner-${idx}`,
                            label: `Banner ${idx + 1}`,
                          })
                        }
                        className={`${adDeleteBtn} text-xs`}
                        disabled={saving || !hasBanner}
                      >
                        <FiTrash2 className="h-3.5 w-3.5" aria-hidden />
                        Delete
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
            <div className="mt-8 border-t border-slate-200/70 pt-6">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-display text-lg font-semibold text-slate-900">Principal image</h3>
                <button
                  type="button"
                  className={`${adPrimaryBtn} text-sm`}
                  onClick={() => {
                    setBannerFile(null);
                    setBannerPreview("");
                    setActiveModal("principal");
                  }}
                >
                  {principalImage ? "Replace Principal Image" : "Upload Principal Image"}
                </button>
              </div>
              <div className="divide-y divide-slate-100 rounded-xl border border-slate-100">
                {!principalImage ? (
                  <div className={`${adEmpty} border-0 py-12 shadow-none`}>No principal image uploaded yet.</div>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-3 px-2 py-3 transition hover:bg-slate-50/80">
                    <span className="font-medium text-slate-800">Current principal image</span>
                    <button
                      type="button"
                      onClick={() =>
                        setConfirmAction({
                          type: "principal-image",
                          id: "principal-image",
                          label: "principal image",
                        })
                      }
                      className={`${adDeleteBtn} text-xs`}
                    >
                      <FiTrash2 className="h-3.5 w-3.5" aria-hidden />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>
        ) : null}
      </main>
      </div>

      <ConfirmDialog
        open={Boolean(confirmAction)}
        title="Delete item?"
        message={
          confirmAction
            ? `Are you sure you want to delete ${confirmAction.label}? This cannot be undone.`
            : ""
        }
        confirmLabel="Delete"
        loading={deleting}
        onConfirm={runConfirmedDelete}
        onCancel={() => !deleting && setConfirmAction(null)}
      />

      <ToastStack toasts={toasts} />

      {activeModal === "trade" ? (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-panel glass-card" onClick={(event) => event.stopPropagation()}>
            <h3>{editingTradeId ? "Edit Trade" : "Add Trade"}</h3>
            <form className="admin-form" onSubmit={submitTrade}>
              <input
                placeholder="Name"
                value={tradeDraft.name}
                onChange={(e) => setTradeDraft({ ...tradeDraft, name: e.target.value })}
                required
              />
              <input
                placeholder="Duration"
                value={tradeDraft.duration}
                onChange={(e) => setTradeDraft({ ...tradeDraft, duration: e.target.value })}
                required
              />
              <textarea
                placeholder="Description"
                value={tradeDraft.description}
                onChange={(e) => setTradeDraft({ ...tradeDraft, description: e.target.value })}
                required
              />
              <label>
                Trade images (max 3 · JPG / PNG / WebP, max 2MB each)
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={(event) => {
                    const selected = Array.from(event.target.files || []);
                    const files = selected.slice(0, 3);
                    if (!files.length) return;
                    if (selected.length > 3) showToast("error", "Select up to 3 images only");
                    setTradeImageFiles(files);
                    setTradePreviews(files.map((f) => URL.createObjectURL(f)));
                  }}
                />
              </label>
              {tradePreviews && tradePreviews.length ? (
                <div className="grid grid-cols-3 gap-2">
                  {tradePreviews.slice(0, 3).map((src, i) => (
                    <img key={i} src={src} alt="Preview" className="preview-image" />
                  ))}
                </div>
              ) : null}
              <p className="admin-hint">New uploads replace existing images. Edits without new files keep current images.</p>
              {renderUploadFeedback()}
              <button type="submit" className={adPrimaryBtn} disabled={saving}>
                {getUploadButtonLabel("Save", "Uploading")}
              </button>
            </form>
          </div>
        </div>
      ) : null}

      {activeModal === "faculty" ? (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-panel glass-card" onClick={(event) => event.stopPropagation()}>
            <h3>{editingFacultyId ? "Edit Faculty" : "Add Faculty"}</h3>
            <form className="admin-form" onSubmit={submitFaculty}>
              <input placeholder="Name" value={facultyDraft.name} onChange={(e) => setFacultyDraft({ ...facultyDraft, name: e.target.value })} required />
              <input placeholder="Qualification" value={facultyDraft.qualification} onChange={(e) => setFacultyDraft({ ...facultyDraft, qualification: e.target.value })} required />
              <input placeholder="Role" value={facultyDraft.role} onChange={(e) => setFacultyDraft({ ...facultyDraft, role: e.target.value })} required />
              <input placeholder="Trade / specialization area" value={facultyDraft.trade} onChange={(e) => setFacultyDraft({ ...facultyDraft, trade: e.target.value })} />
              <input placeholder="Experience highlight (e.g. 10+ Years in Electrical)" value={facultyDraft.experienceText} onChange={(e) => setFacultyDraft({ ...facultyDraft, experienceText: e.target.value })} />
              <input placeholder="Specialization" value={facultyDraft.specialization} onChange={(e) => setFacultyDraft({ ...facultyDraft, specialization: e.target.value })} />
              <input placeholder="Years (optional short)" value={facultyDraft.experienceYears} onChange={(e) => setFacultyDraft({ ...facultyDraft, experienceYears: e.target.value })} />
              <label>
                Photo (JPG / PNG / WebP, max 2MB)
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    setFacultyImageFile(file);
                    setFacultyPreview(URL.createObjectURL(file));
                  }}
                />
              </label>
              {facultyPreview ? <img src={facultyPreview} alt="Preview" className="preview-image" /> : null}
              {renderUploadFeedback()}
              <button type="submit" className={adPrimaryBtn} disabled={saving}>
                {getUploadButtonLabel("Save", "Uploading")}
              </button>
            </form>
          </div>
        </div>
      ) : null}

      {activeModal === "gallery" ? (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-panel glass-card" onClick={(event) => event.stopPropagation()}>
            <h3>{editingGalleryId ? "Edit Gallery Image" : "Add Gallery Image"}</h3>
            <form className="admin-form" onSubmit={submitGallery}>
              <label>
                Category
                <select
                  value={galleryDraft.category}
                  onChange={(e) => setGalleryDraft({ ...galleryDraft, category: e.target.value })}
                >
                  {GALLERY_CATS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <input
                placeholder="Title"
                value={galleryDraft.title}
                onChange={(e) => setGalleryDraft({ ...galleryDraft, title: e.target.value })}
                required
              />
              <label>
                Image (JPG / PNG / WebP, max 2MB)
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    setGalleryImageFile(file);
                    setGalleryPreview(URL.createObjectURL(file));
                  }}
                />
              </label>
              {galleryPreview ? <img src={galleryPreview} alt="Preview" className="preview-image" /> : null}
              {renderUploadFeedback()}
              <button type="submit" className={adPrimaryBtn} disabled={saving}>
                {getUploadButtonLabel("Save", "Uploading")}
              </button>
            </form>
          </div>
        </div>
      ) : null}

      {activeModal === "gallery-batch" ? (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-panel glass-card" onClick={(event) => event.stopPropagation()}>
            <h3>Upload multiple gallery images</h3>
            <form className="admin-form" onSubmit={submitGalleryBatch}>
              <label>
                Category
                <select
                  value={galleryBatch.category}
                  onChange={(e) => setGalleryBatch((b) => ({ ...b, category: e.target.value }))}
                >
                  {GALLERY_CATS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <input
                placeholder="Title prefix (e.g. Campus)"
                value={galleryBatch.titlePrefix}
                onChange={(e) => setGalleryBatch((b) => ({ ...b, titlePrefix: e.target.value }))}
              />
              <label>
                Images (multiple)
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={(event) => {
                    const files = Array.from(event.target.files || []);
                    setGalleryBatch((b) => ({ ...b, files }));
                  }}
                />
              </label>
              {renderUploadFeedback()}
              <button type="submit" className={adPrimaryBtn} disabled={saving}>
                {getUploadButtonLabel("Upload all", "Uploading")}
              </button>
            </form>
          </div>
        </div>
      ) : null}

      {activeModal === "testimonial" ? (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-panel glass-card" onClick={(event) => event.stopPropagation()}>
            <h3>{editingTestimonialId ? "Edit Student Success Story" : "Add Student Success Story"}</h3>
            <form className="admin-form" onSubmit={submitTestimonial}>
              <input
                placeholder="Name (optional)"
                value={testimonialDraft.name}
                onChange={(e) => setTestimonialDraft((p) => ({ ...p, name: e.target.value }))}
              />
              <textarea
                placeholder="Text (optional)"
                value={testimonialDraft.text}
                onChange={(e) => setTestimonialDraft((p) => ({ ...p, text: e.target.value }))}
              />
              <label>
                Image (required · JPG / PNG / WebP, max 2MB)
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    setTestimonialImageFile(file);
                    setTestimonialPreview(URL.createObjectURL(file));
                  }}
                />
              </label>
              {testimonialPreview ? <img src={testimonialPreview} alt="Preview" className="preview-image" /> : null}
              {renderUploadFeedback()}
              <button type="submit" className={adPrimaryBtn} disabled={saving}>
                {getUploadButtonLabel("Save", "Uploading")}
              </button>
            </form>
          </div>
        </div>
      ) : null}

      {activeModal === "banner" ? (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-panel glass-card" onClick={(event) => event.stopPropagation()}>
            <h3>
              {bannerImages[bannerTargetIndex]?.image
                ? `Replace Banner ${bannerTargetIndex + 1}`
                : `Upload Banner ${bannerTargetIndex + 1}`}
            </h3>
            <form
              className="admin-form"
              onSubmit={async (event) => {
                event.preventDefault();
                if (saving) return;
                if (!bannerFile) {
                  showToast("error", "Choose an image file");
                  return;
                }
                const fd = new FormData();
                fd.append("image", bannerFile);
                fd.append("index", String(bannerTargetIndex));
                beginUpload(formatUploadFileLabel(bannerFile, `banner-${bannerTargetIndex + 1}`));
                try {
                  const response = bannerImages[bannerTargetIndex]?.image
                    ? await updateBannerImageAt(bannerTargetIndex, fd, uploadConfig)
                    : await addBannerImage(fd, uploadConfig);
                  setBannerImages(Array.isArray(response?.bannerImages) ? response.bannerImages : []);
                  await finishUpload();
                  closeModal();
                  showToast("success", "Banner saved");
                } catch (err) {
                  setUploadError("Banner upload failed.");
                  showToast("error", err?.response?.data?.message || "Could not save banner");
                } finally {
                  setSaving(false);
                  resetUploadState();
                }
              }}
            >
              <label>
                Banner image
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    setBannerFile(file);
                    setBannerPreview(URL.createObjectURL(file));
                  }}
                />
              </label>
              {bannerPreview ? <img src={bannerPreview} alt="Preview" className="preview-image preview-banner" /> : null}
              {renderUploadFeedback()}
              <button type="submit" className={adPrimaryBtn} disabled={saving}>
                {getUploadButtonLabel("Save", "Uploading")}
              </button>
            </form>
          </div>
        </div>
      ) : null}
      {activeModal === "principal" ? (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-panel glass-card" onClick={(event) => event.stopPropagation()}>
            <h3>{principalImage ? "Replace Principal Image" : "Upload Principal Image"}</h3>
            <form
              className="admin-form"
              onSubmit={async (event) => {
                event.preventDefault();
                if (saving) return;
                if (!bannerFile) {
                  showToast("error", "Choose an image file");
                  return;
                }
                const fd = new FormData();
                fd.append("image", bannerFile);
                beginUpload(formatUploadFileLabel(bannerFile, "principal-image"));
                try {
                  const site = await updatePrincipalImage(fd, uploadConfig);
                  setPrincipalImage(site?.principalImage || "");
                  await finishUpload();
                  closeModal();
                  showToast("success", "Principal image saved");
                } catch (err) {
                  setUploadError("Principal image upload failed.");
                  showToast("error", err?.response?.data?.message || "Could not save principal image");
                } finally {
                  setSaving(false);
                  resetUploadState();
                }
              }}
            >
              <label>
                Principal image
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    setBannerFile(file);
                    setBannerPreview(URL.createObjectURL(file));
                  }}
                />
              </label>
              {bannerPreview ? <img src={bannerPreview} alt="Preview" className="preview-image preview-banner" /> : null}
              {renderUploadFeedback()}
              <button type="submit" className={adPrimaryBtn} disabled={saving}>
                {getUploadButtonLabel("Save", "Uploading")}
              </button>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default AdminDashboard;
